import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createContext, Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// Lightweight markdown renderer tailored for assistant responses. Avoids a new
// dependency by handling the subset of Markdown the model actually emits:
// paragraphs, blockquotes, headings, lists, fenced code, and inline `**bold**`
// `*italic*` `` `code` `` `[text](url)` `~~strike~~`. Blockquotes get a closer
// look so we can render Quran verses and Hadith citations in their own block
// styles — matching the landing-page conversation card (`CitedQuote`) and the
// design's verse treatment.

interface MarkdownContentProps {
  content: string;
  /** Send a follow-up question to the conversation — wired to in-block chips
   *  like the Hadith "View chain" action. Absent ⇒ those chips stay inert. */
  onAskFollowUp?: (text: string) => void;
  /** A stream is already in flight — disables follow-up chips. */
  busy?: boolean;
}

// Lets nested blocks (e.g. `HadithBlock`) reach the conversation's follow-up
// sender without drilling a callback through the `renderBlock` switch.
interface MarkdownActions {
  askFollowUp?: (text: string) => void;
  busy: boolean;
}
const MarkdownActionsContext = createContext<MarkdownActions>({ busy: false });

export function MarkdownContent({ content, onAskFollowUp, busy = false }: MarkdownContentProps) {
  const blocks = parseBlocks(content);
  return (
    <MarkdownActionsContext.Provider value={{ askFollowUp: onAskFollowUp, busy }}>
      <Box>
        {blocks.map((block, i) => (
          <Fragment key={i}>{renderBlock(block, i)}</Fragment>
        ))}
      </Box>
    </MarkdownActionsContext.Provider>
  );
}

// ---------- block model ----------

type HadithGrade = 'sahih' | 'hasan' | 'daif' | null;

type Block =
  | { kind: 'heading'; level: 1 | 2 | 3 | 4 | 5 | 6; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'code'; lang: string | null; code: string }
  | { kind: 'hr' }
  | {
      kind: 'quote';
      flavor: 'quran' | 'hadith' | 'plain';
      body: string;
      citation: string | null;
      grade: HadithGrade;
    };

// Ordered-list item marker: a digit run followed by `.` or `)`. The digit
// class covers ASCII 0-9, Arabic-Indic ٠-٩ (U+0660–0669), and Extended
// Arabic-Indic ۰-۹ (U+06F0–06F9) — Arabic answers number with those glyphs,
// so an ASCII-only `\d` would miss the whole list and fall back to paragraphs.
const ORDERED_ITEM_RE = /^\s*[\d٠-٩۰-۹]+[.)]\s+/;

// First-line box height of list-item text (fontSize 14.5 × lineHeight 1.7).
// A marker wrapper of this height + `alignItems: center` centres the bullet
// dot / number badge against line 1 of the item.
const LIST_LINE_HEIGHT_PX = 24.65;

function parseBlocks(input: string): Block[] {
  const lines = input.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    // fenced code: ```lang? ... ```
    const fence = /^```(\w*)\s*$/.exec(line);
    if (fence) {
      const lang = fence[1] || null;
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```\s*$/.test(lines[i]!)) {
        buf.push(lines[i]!);
        i++;
      }
      if (i < lines.length) i++; // consume closing fence
      blocks.push({ kind: 'code', lang, code: buf.join('\n') });
      continue;
    }

    // horizontal rule
    if (/^\s*([-_*])\s*\1\s*\1[\s\S]*$/.test(line) && /^\s*[-_*]{3,}\s*$/.test(line)) {
      blocks.push({ kind: 'hr' });
      i++;
      continue;
    }

    // heading
    const heading = /^(#{1,6})\s+(.+?)\s*#*\s*$/.exec(line);
    if (heading) {
      const level = heading[1]!.length as 1 | 2 | 3 | 4 | 5 | 6;
      blocks.push({ kind: 'heading', level, text: heading[2]! });
      i++;
      continue;
    }

    // blockquote — consume contiguous `>`-prefixed lines (and their continuations)
    if (/^\s*>/.test(line)) {
      const quoteLines: string[] = [];
      while (
        i < lines.length &&
        (/^\s*>/.test(lines[i]!) ||
          (lines[i]!.trim() === '' && i + 1 < lines.length && /^\s*>/.test(lines[i + 1]!)))
      ) {
        quoteLines.push(lines[i]!.replace(/^\s*>\s?/, ''));
        i++;
      }
      blocks.push(classifyQuote(quoteLines.join('\n').trim()));
      continue;
    }

    // unordered list
    if (/^\s*[-*+]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*+]\s+/.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^\s*[-*+]\s+/, ''));
        i++;
      }
      blocks.push({ kind: 'list', ordered: false, items });
      continue;
    }

    // ordered list
    if (ORDERED_ITEM_RE.test(line)) {
      const items: string[] = [];
      while (i < lines.length && ORDERED_ITEM_RE.test(lines[i]!)) {
        items.push(lines[i]!.replace(ORDERED_ITEM_RE, ''));
        i++;
      }
      blocks.push({ kind: 'list', ordered: true, items });
      continue;
    }

    // blank line separates paragraphs
    if (line.trim() === '') {
      i++;
      continue;
    }

    // paragraph — consume until blank line or a new block-starter
    const paraBuf: string[] = [line];
    i++;
    while (i < lines.length) {
      const nxt = lines[i]!;
      if (nxt.trim() === '') break;
      if (/^(#{1,6}\s+|```|\s*>|\s*[-*+]\s+|\s*[\d٠-٩۰-۹]+[.)]\s+|\s*[-_*]{3,}\s*$)/.test(nxt))
        break;
      paraBuf.push(nxt);
      i++;
    }
    // A "pure-Quran" paragraph (verse glyphs + citation) renders as a verse block too.
    const paraText = paraBuf.join('\n').trim();
    if (looksLikePlainQuran(paraText)) {
      blocks.push(classifyQuote(paraText));
    } else {
      blocks.push({ kind: 'paragraph', text: paraText });
    }
  }

  return blocks;
}

// ---------- Quran / Hadith detection ----------

// Verse-end glyphs ﴿ ﴾ are the strongest signal; otherwise we look for a
// citation marker on the trailing line.
const QURAN_GLYPH_RE = /[۝﴾﴿]/; // ۝ ﴿ ﴾ plus the surah-number indicator
const QURAN_ENCLOSURES_RE = /[﴾﴿]/; // ﴿ ﴾
// `\b` only knows ASCII word chars, so it never fires next to Arabic script —
// an Arabic citation (سورة …, سنن الترمذي …) would slip past detection. The
// `\p{L}` (any-letter) lookarounds below are the Unicode-correct boundary, so
// Arabic hadith citations classify as `hadith` instead of falling to `plain`.
const QURAN_CITATION_RE =
  /\(?\s*(?:Quran|Qur['']an|Surah|Surat(?:ul)?|Al-Qur['']an|سورة|السورة|القرآن|قرآن)(?![\p{L}])[^)\n]*?\d+\s*[:،]\s*\d+/iu;
const HADITH_CITATION_RE =
  /(?<![\p{L}])(?:Sahih\s+)?(?:Bukhari|Muslim|Tirmidhi|Abu\s+Dawud|Nasa['’]?i|Ibn\s+Majah|Ahmad|Musnad\s+Ahmad|Muwatta|Bayhaqi|Hakim|البخاري|مسلم|الترمذي|أبو\s+داود|النسائي|ابن\s+ماجه|أحمد|الموطأ|البيهقي|الحاكم)(?![\p{L}])/iu;

// Detect a hadith grading label anywhere in the quote body or citation. Order
// matters — "sahih" must beat "hasan" (since "hasan sahih" exists) and "daif"
// has multiple romanizations.
function detectHadithGrade(text: string): HadithGrade {
  if (/\b(?:sahih)\b|صحيح/i.test(text)) return 'sahih';
  if (/\b(?:hasan)\b|حسن/i.test(text)) return 'hasan';
  if (/\b(?:da['’]?if|daif|weak)\b|ضعيف/i.test(text)) return 'daif';
  return null;
}

function looksLikePlainQuran(text: string): boolean {
  if (!QURAN_ENCLOSURES_RE.test(text) && !QURAN_GLYPH_RE.test(text)) return false;
  return QURAN_CITATION_RE.test(text) || /[؀-ۿ]/.test(text);
}

function classifyQuote(body: string): Block & { kind: 'quote' } {
  // Strip trailing citation line into its own field when we can spot one.
  const lines = body.split('\n');
  let citation: string | null = null;
  const last = lines[lines.length - 1]?.trim() ?? '';

  // A short trailing line that looks like just a citation `(Quran 1:1)` /
  // `— Sahih al-Bukhari 1` is pulled out; longer ones stay inline.
  if (
    last &&
    last.length <= 80 &&
    (QURAN_CITATION_RE.test(last) || HADITH_CITATION_RE.test(last) || /^[—–-]\s*\S/.test(last))
  ) {
    citation = last
      .replace(/^[—–-]\s*/, '')
      .replace(/^\((.*)\)$/, '$1')
      .trim();
    lines.pop();
  }

  const stripped = lines.join('\n').trim();
  const flavor: 'quran' | 'hadith' | 'plain' =
    QURAN_CITATION_RE.test(body) || QURAN_ENCLOSURES_RE.test(body) || QURAN_GLYPH_RE.test(body)
      ? 'quran'
      : HADITH_CITATION_RE.test(body)
        ? 'hadith'
        : 'plain';

  const grade = flavor === 'hadith' ? detectHadithGrade(`${body}\n${citation ?? ''}`) : null;

  return { kind: 'quote', flavor, body: stripped, citation, grade };
}

// ---------- plain-text serialization ----------

// Drop inline markdown markers, keeping the visible text: `**b**`/`*i*` →
// text, `` `code` `` → code, `~~s~~` → s, `[label](url)` → label.
function stripInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
}

// Serializes assistant markdown to clean plain text — used by the Copy action
// so the clipboard gets readable prose, not `**`/`>`/`#` syntax. Reuses the
// same `parseBlocks` the renderer runs on, so the two never drift.
export function markdownToPlainText(content: string): string {
  return parseBlocks(content)
    .map((block): string => {
      switch (block.kind) {
        case 'heading':
        case 'paragraph':
          return stripInlineMarkdown(block.text);
        case 'list':
          return block.items
            .map((item, idx) =>
              block.ordered
                ? `${idx + 1}. ${stripInlineMarkdown(item)}`
                : `- ${stripInlineMarkdown(item)}`,
            )
            .join('\n');
        case 'code':
          return block.code;
        case 'hr':
          return '';
        case 'quote': {
          const body = stripInlineMarkdown(block.body);
          return block.citation ? `${body}\n${stripInlineMarkdown(block.citation)}` : body;
        }
      }
    })
    .filter((part) => part.length > 0)
    .join('\n\n')
    .trim();
}

// ---------- rendering ----------

function renderBlock(block: Block, _key: number): ReactNode {
  switch (block.kind) {
    case 'paragraph':
      return (
        <Typography
          component="p"
          // `dir="auto"` — direction follows the text's first strong char, so
          // an Arabic answer reads RTL even in an English UI and vice versa.
          dir="auto"
          sx={{
            my: 1,
            fontSize: 14.5,
            lineHeight: 1.75,
            color: 'text.primary',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {renderInline(block.text)}
        </Typography>
      );
    case 'heading': {
      const sizeMap: Record<number, number> = { 1: 22, 2: 19, 3: 17, 4: 15.5, 5: 14.5, 6: 13.5 };
      return (
        <Typography
          component={`h${block.level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'}
          dir="auto"
          sx={{
            mt: 2,
            mb: 0.75,
            fontSize: sizeMap[block.level] ?? 15,
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: 'text.primary',
          }}
        >
          {renderInline(block.text)}
        </Typography>
      );
    }
    case 'list':
      if (block.ordered) {
        return (
          <Stack component="ol" spacing={1} sx={{ my: 1, p: 0, listStyle: 'none' }}>
            {block.items.map((item, idx) => (
              <Stack
                component="li"
                key={idx}
                dir="auto"
                direction="row"
                alignItems="flex-start"
                // CSS `gap`, not Stack `spacing`: `spacing` derives its margin
                // from the theme direction, so on RTL content in an LTR UI the
                // gap lands on the wrong side. `gap` is direction-agnostic.
                sx={{ gap: 1 }}
              >
                {/* Badge in a first-line-height wrapper so it centres on
                    line 1 — same technique as the bullet dot. */}
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    height: `${LIST_LINE_HEIGHT_PX}px`,
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: `color-mix(in srgb, ${theme.palette.primary.main} 14%, transparent)`,
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 12,
                      fontWeight: 700,
                    })}
                  >
                    {idx + 1}
                  </Box>
                </Box>
                <Box sx={{ fontSize: 14.5, lineHeight: 1.7, color: 'text.primary' }}>
                  {renderInline(item)}
                </Box>
              </Stack>
            ))}
          </Stack>
        );
      }
      // Flex rows with a custom dot rather than a native `<ul>`. Native
      // `list-style-position: outside` bullets render in negative space and
      // overflow the card when the list direction doesn't match RTL content;
      // a flex child can't escape the row, and `spacing` gives a clean gap.
      return (
        <Stack component="ul" spacing={0.75} sx={{ my: 1, p: 0, listStyle: 'none' }}>
          {block.items.map((item, idx) => (
            <Stack
              component="li"
              key={idx}
              dir="auto"
              direction="row"
              alignItems="flex-start"
              // CSS `gap`, not Stack `spacing` — see the ordered list above.
              sx={{ gap: 2 }}
            >
              {/* The dot sits in a wrapper whose height equals the text's
                  first-line box, so `alignItems: center` centres the dot on
                  line 1 — no magic top-margin to drift if metrics change. */}
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  height: `${LIST_LINE_HEIGHT_PX}px`,
                }}
              >
                <Box
                  sx={(theme) => ({
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                  })}
                />
              </Box>
              <Box sx={{ fontSize: 14.5, lineHeight: 1.7, color: 'text.primary' }}>
                {renderInline(item)}
              </Box>
            </Stack>
          ))}
        </Stack>
      );
    case 'code':
      return (
        <Box
          component="pre"
          dir="ltr"
          sx={(theme) => ({
            my: 1.5,
            p: 1.5,
            borderRadius: (t) => `${t.shape.borderRadius}px`,
            border: 1,
            borderColor: 'divider',
            backgroundColor: theme.palette.action.hover,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: 12.5,
            lineHeight: 1.55,
            overflowX: 'auto',
          })}
        >
          <code>{block.code}</code>
        </Box>
      );
    case 'hr':
      return <Box component="hr" sx={{ my: 2, border: 0, borderTop: 1, borderColor: 'divider' }} />;
    case 'quote':
      if (block.flavor === 'quran')
        return <QuranBlock body={block.body} citation={block.citation} />;
      if (block.flavor === 'hadith')
        return <HadithBlock body={block.body} citation={block.citation} grade={block.grade} />;
      return <PlainQuote body={block.body} citation={block.citation} />;
  }
}

// ---------- Quran recitation audio (alquran.cloud) ----------

// Quran verse card. Sage-tinted background, thick green inset on the start
// edge, Amiri Arabic right-aligned and floated to the trailing edge,
// translation underneath, and a citation row (em-dash prefix).
// Matches the chat design bundle's verse treatment 1:1.
function QuranBlock({ body, citation }: { body: string; citation: string | null }) {
  const segments = splitArabicSegments(body);

  // Lay the card out by the response language, not the UI theme: an Arabic
  // citation ⇒ RTL. A Quran verse is always Arabic, so default to RTL when
  // there is no citation to judge by.
  const direction: 'rtl' | 'ltr' = citation && !/[؀-ۿ]/.test(citation) ? 'ltr' : 'rtl';

  return (
    <Box
      dir={direction}
      sx={(theme) => ({
        my: 2,
        position: 'relative',
        borderRadius: (t) => `${t.shape.borderRadius}px`,
        overflow: 'hidden',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? `color-mix(in srgb, ${theme.palette.primary.main} 14%, ${theme.palette.background.paper})`
            : `color-mix(in srgb, ${theme.palette.primary.main} 8%, ${theme.palette.background.paper})`,
        border: 1,
        borderColor: `color-mix(in srgb, ${theme.palette.primary.main} 32%, transparent)`,
        // Accent bar on the reading-start edge — right for RTL, left for LTR.
        boxShadow: `inset ${direction === 'rtl' ? '-4px' : '4px'} 0 0 ${theme.palette.primary.main}`,
        paddingBlock: 2,
        paddingInline: 2.25,
      })}
    >
      {segments.map((seg, idx) =>
        seg.kind === 'arabic' ? (
          <Typography
            key={idx}
            lang="ar"
            dir="rtl"
            sx={{
              fontFamily: '"Amiri", "Cairo Variable", serif',
              fontSize: 14.5,
              lineHeight: 1.7,
              color: 'text.primary',
              unicodeBidi: 'isolate',
              fontWeight: 600,
              // The Arabic verse aligns to its own reading start (right) —
              // `dir="rtl"` handles it; no explicit textAlign needed.
              my: 0.5,
            }}
          >
            {seg.text}
          </Typography>
        ) : (
          <Typography
            key={idx}
            dir="auto"
            sx={{
              fontSize: 14.5,
              lineHeight: 1.7,
              fontStyle: 'italic',
              color: 'text.secondary',
              my: 0.5,
              whiteSpace: 'pre-wrap',
            }}
          >
            {renderInline(seg.text)}
          </Typography>
        ),
      )}

      {citation && (
        <Typography
          sx={(theme) => ({
            mt: 1.5,
            fontSize: 13,
            fontWeight: 600,
            color: theme.palette.primary.main,
            letterSpacing: '0.01em',
          })}
        >
          {`— ${citation}`}
        </Typography>
      )}
    </Box>
  );
}

function HadithBlock({
  body,
  citation,
  grade,
}: {
  body: string;
  citation: string | null;
  grade: HadithGrade;
}) {
  const { t } = useTranslation('chat');
  const strippedBody = stripGradePrefix(body);

  // Lay the card out by the response language, not the UI theme — Arabic
  // hadith text/citation ⇒ RTL. Unlike a Quran verse, a hadith can be in
  // either language, so this is detected from the content rather than fixed.
  const direction: 'rtl' | 'ltr' = /[؀-ۿ]/.test(citation ?? strippedBody) ? 'rtl' : 'ltr';

  return (
    <Box
      dir={direction}
      sx={(theme) => ({
        my: 2,
        // Accent line on the reading-start edge. `borderInlineStart` is a CSS
        // *logical* property: it resolves against the card's `dir`, so it is
        // physically the RIGHT edge for an RTL hadith and the LEFT edge for an
        // LTR one — guaranteed, with no offset sign to get wrong. (A box-shadow
        // offset is physical and cannot follow `dir`.)
        borderInlineStart: `3px solid ${theme.palette.primary.main}`,
        backgroundColor: 'background.paper',
        paddingBlock: 1.75,
        paddingInline: 2.25,
      })}
    >
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
        <Box
          sx={(theme) => ({
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
          })}
        />
        <Typography
          sx={(theme) => ({
            fontSize: 11.5,
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: theme.palette.primary.main,
          })}
        >
          {t('messages.blocks.hadith')}
        </Typography>
        {grade && (
          <Box
            sx={(theme) => ({
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: theme.palette.primary.main,
              backgroundColor: `color-mix(in srgb, ${theme.palette.primary.main} 14%, transparent)`,
              border: 1,
              borderColor: `color-mix(in srgb, ${theme.palette.primary.main} 32%, transparent)`,
              paddingBlock: '2px',
              paddingInline: '8px',
              borderRadius: (t) => `${t.shape.borderRadius}px`,
              lineHeight: 1.4,
            })}
          >
            {t(`messages.blocks.grading.${grade}`)}
          </Box>
        )}
      </Stack>
      <Typography
        dir="auto"
        sx={{
          fontSize: 14.5,
          lineHeight: 1.75,
          fontStyle: 'italic',
          color: 'text.primary',
          whiteSpace: 'pre-wrap',
        }}
      >
        {renderInline(strippedBody)}
      </Typography>
      {citation && (
        <Typography
          dir="auto"
          sx={{ mt: 1.5, fontSize: 13, fontWeight: 700, color: 'text.primary' }}
        >
          {citation}
        </Typography>
      )}
    </Box>
  );
}

// Trim grading words ("Sahih:", "(Sahih)", "صحيح —" …) off the front of a
// hadith body when the same grade is being rendered as a pill — avoids
// duplicating it inside the quote text.
function stripGradePrefix(body: string): string {
  return body
    .replace(
      /^\s*[([]?\s*(?:sahih|hasan|da['’]?if|daif|weak|صحيح|حسن|ضعيف)\s*[)\]]?\s*[:—–-]?\s*/i,
      '',
    )
    .trimStart();
}

function PlainQuote({ body, citation }: { body: string; citation: string | null }) {
  return (
    <Box
      sx={(theme) => ({
        my: 1.5,
        borderInlineStart: `2.5px solid ${theme.palette.primary.main}`,
        paddingInlineStart: 1.75,
        color: 'text.secondary',
        fontStyle: 'italic',
      })}
    >
      <Typography dir="auto" sx={{ fontSize: 14, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
        {renderInline(body)}
      </Typography>
      {citation && (
        <Typography dir="auto" sx={{ fontSize: 12, mt: 0.5, fontStyle: 'normal' }}>
          {citation}
        </Typography>
      )}
    </Box>
  );
}

// ---------- inline ----------

// Splits a quote's body into alternating Arabic and non-Arabic segments so we
// can apply Amiri + RTL only to the Arabic portions while keeping translations
// in the regular text style.
function splitArabicSegments(text: string): Array<{ kind: 'arabic' | 'other'; text: string }> {
  const out: Array<{ kind: 'arabic' | 'other'; text: string }> = [];
  const lines = text.split('\n');
  let buf: string[] = [];
  let bufKind: 'arabic' | 'other' | null = null;

  const flush = () => {
    if (buf.length && bufKind) out.push({ kind: bufKind, text: buf.join('\n').trim() });
    buf = [];
    bufKind = null;
  };

  for (const line of lines) {
    const kind: 'arabic' | 'other' = /[؀-ۿ]/.test(line) ? 'arabic' : 'other';
    if (bufKind === null) bufKind = kind;
    if (kind !== bufKind) {
      flush();
      bufKind = kind;
    }
    buf.push(line);
  }
  flush();
  return out.filter((s) => s.text.length > 0);
}

// Inline markdown: **bold**, *italic*, `code`, [text](url), ~~strike~~. Plus a
// safety net for raw URLs. Order matters — escape HTML-y chars never enter the
// React tree because we always go through React.createElement, not innerHTML.
function renderInline(text: string): ReactNode {
  const nodes: ReactNode[] = [];
  let rest = text;
  let key = 0;

  const tokenRe =
    /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)|(~~([^~]+)~~)|(\[([^\]]+)\]\(([^)]+)\))/;
  while (rest.length > 0) {
    const match = tokenRe.exec(rest);
    if (!match) {
      nodes.push(rest);
      break;
    }
    if (match.index > 0) nodes.push(rest.slice(0, match.index));
    if (match[1]) {
      nodes.push(
        <Box component="strong" key={`b-${key}`} sx={{ fontWeight: 700 }}>
          {match[2]}
        </Box>,
      );
    } else if (match[3]) {
      nodes.push(
        <Box component="em" key={`i-${key}`} sx={{ fontStyle: 'italic' }}>
          {match[4]}
        </Box>,
      );
    } else if (match[5]) {
      nodes.push(
        <Box
          component="code"
          key={`c-${key}`}
          sx={(theme) => ({
            paddingInline: '6px',
            paddingBlock: '2px',
            borderRadius: (t) => `${t.shape.borderRadius}px`,
            backgroundColor: theme.palette.action.selected,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: '0.92em',
          })}
        >
          {match[6]}
        </Box>,
      );
    } else if (match[7]) {
      nodes.push(
        <Box
          component="span"
          key={`s-${key}`}
          sx={{ textDecoration: 'line-through', opacity: 0.7 }}
        >
          {match[8]}
        </Box>,
      );
    } else if (match[9]) {
      nodes.push(
        <Box
          component="a"
          key={`l-${key}`}
          href={match[11]}
          target="_blank"
          rel="noopener noreferrer"
          sx={(theme) => ({
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          })}
        >
          {match[10]}
        </Box>,
      );
    }
    rest = rest.slice(match.index + match[0].length);
    key++;
  }
  return nodes;
}
